import styled from '@emotion/styled'
import { Box, Card, CardContent, CardHeader, Stack, TextField, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ReactComponent as GenealogyFilterIcon } from './assets/GenealogyFilterIcon.svg'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { GenealogyAPI } from './GenealogyAPIs'
import storeAuth from 'GlobalStores/User/StoreAuth'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import { get } from 'lodash'
import { getNativeName } from 'Helpers/GlobalHelpers'
import GenealogyCardItemName from './GenealogyCardItemName'
import GenealogyCardSkeleton from './GenealogyCardSkeleton'
import GenealogyColumn from './GenealogyColumn'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { ReactComponent as DropdownIcon } from './assets/Dropdown.svg'
import GenealogyFilterModal from './GenealogyFilterModal'
import GenealogyHistoryModal from './GenealogyHistoryModal'
import StoreGenealogyFilter from './StoreGenealogyFilter'
import { runInAction } from 'mobx'
import { storeElement } from 'GlobalStores/StoreElement'

const GenealogyCard = observer(() => {
    const translate = useTranslate()
    const { customerId } = useParams()
    const navigate = useNavigate()
    const [achievementHistory, setAchievementHistory] = useState([])
    const [genealogyItems, setGenealogyItems] = useState([])
    const [genealogyPeriods, setGenealogyPeriods] = useState([])
    const [genealogyLoading, setGenealogyLoading] = useState(false)
    const [selectedPeriod, setSelectedPeriod] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [isRefreshing, setIsRefreshing] = useState(false)

    // Check if viewing downline (has customerId in URL and not the logged-in user)
    const isDownline = customerId && customerId !== storeAuth.GetId()
    const currentBaId = customerId || storeAuth.GetId()

    const handlePreviousPeriod = () => {
        const currentIndex = genealogyPeriods.indexOf(selectedPeriod)
        if (currentIndex < genealogyPeriods.length - 1) {
            setSelectedPeriod(genealogyPeriods[currentIndex + 1])
        }
    }

    const handleNextPeriod = () => {
        const currentIndex = genealogyPeriods.indexOf(selectedPeriod)
        if (currentIndex > 0) {
            setSelectedPeriod(genealogyPeriods[currentIndex - 1])
        }
    }

    const handleSearch = e => {
        setSearchQuery(e.target.value)
    }

    const fetchUserFilter = async () => {
        try {
            const response = await GenealogyAPI.FetchUserFilter({
                baId: currentBaId,
                ushopCountryCode: storeCountry.Country3(),
                token: storeAuth.GetToken()
            })

            StoreGenealogyFilter.setFilterOptions({
                showAll: response.data.data.showAll,
                showDistributor: response.data.data.showDistributor,
                showMember: response.data.data.showMember
            })

            runInAction(() => {
                StoreGenealogyFilter.hideInactiveLegs = !response.data.data.showActiveLeg
                StoreGenealogyFilter.sortByOv = response.data.data.sortByOv
            })
        } catch (error) {
            console.error(error)
        }
    }

    const filteredGenealogyItems = genealogyItems.filter((item, index) => {
        // Always keep the first item (index 0)
        if (index === 0) return true
        if (!searchQuery) return true

        const query = searchQuery.toLowerCase()
        const fullNameEn = get(item, 'customer.humanName.fullName', '').toLowerCase()
        const fullNameNative = getNativeName(get(item, 'customer.humanName', '')).toLowerCase()
        const customerId = get(item, 'customer.id.unicity', '').toString().toLowerCase()

        return fullNameEn.includes(query) || fullNameNative.includes(query) || customerId.includes(query)
    })

    const filteredMetricsProfileHistory = selectedPeriod
        ? filteredGenealogyItems
              .map(item => {
                  const metricsItems = get(item, 'customer.metricsProfileHistory.items', [])
                  const periodItem = metricsItems.find(m => m.period === selectedPeriod)
                  return periodItem ? periodItem.value : null
              })
              .filter(Boolean)
        : []

    const getCustomerType = () => {
        if (StoreGenealogyFilter.getFilterOptions('showAll')) {
            return 'all'
        } else if (StoreGenealogyFilter.getFilterOptions('showDistributor')) {
            return 'distributor'
        } else if (StoreGenealogyFilter.getFilterOptions('showMember')) {
            return 'member'
        }
    }

    const getGenealogyItems = async (byPassCache = false) => {
        try {
            window.scrollTo(0, 0)
            setGenealogyLoading(true)
            const response = await GenealogyAPI.GetGenealogyItems({
                baId: currentBaId,
                ushopCountryCode: storeCountry.Country3(),
                token: storeAuth.GetToken(),
                options: StoreGenealogyFilter.getFilterOptionsString(),
                customerType: getCustomerType(),
                byPassCache
            })

            const items = get(response, 'data.ushop.genealogy.items', [])
            const achievementsHistory = get(response, 'data.ushop.achievementsHistory', {
                isRequestTimeout: false,
                items: []
            })
            const periods = achievementsHistory.items.map(item => item.period)

            setGenealogyItems(items)
            setGenealogyPeriods(periods)
            setAchievementHistory(achievementsHistory)
            setGenealogyLoading(false)
            StoreGenealogyFilter.setDirty(false)
            StoreGenealogyFilter.setShouldRefetch(false)
            StoreGenealogyFilter.setShouldRefresh(false)
        } catch (error) {
            console.error(error)
            setGenealogyLoading(false)
        }
    }

    const handleFetchDownline = targetCustomerId => {
        // Find the customer info from genealogyItems
        const customerItem = genealogyItems.find(item => get(item, 'customer.id.unicity') === targetCustomerId)
        if (customerItem) {
            const customerName = get(customerItem, 'customer.humanName.fullName', '')
            const nativeName = getNativeName(get(customerItem, 'customer.humanName', ''))
            const profilePicture = get(customerItem, 'customer.profilePicture', '')
            const highestRankShort = get(customerItem, 'customer.cumulativeMetricsProfile.highestRankShort', '')
            const isSuspended = ['Terminated', 'Suspended', 'Retired'].includes(get(customerItem, 'customer.status', ''))
            StoreGenealogyFilter.addToHistory({
                customerId: targetCustomerId,
                customerName,
                nativeName,
                profilePicture,
                highestRankShort,
                isSuspended
            })
        }
        navigate(`/user/genealogy/${targetCustomerId}`)
    }

    const handleGoBack = () => {
        window.scrollTo(0, 0)
        navigate(-1)
    }

    const saveGenealogyFilter = async () => {
        try {
            await GenealogyAPI.SaveGenealogyFilter({
                baId: currentBaId,
                sortByOv: StoreGenealogyFilter.sortByOv,
                showActiveLeg: !StoreGenealogyFilter.hideInactiveLegs,
                showAll: StoreGenealogyFilter.showAll,
                showDistributor: StoreGenealogyFilter.showDistributor,
                showMember: StoreGenealogyFilter.showMember,
                token: storeAuth.GetToken()
            })
        } catch (error) {
            console.error(error)
        }
    }

    const fetchData = async () => {
        await getGenealogyItems()
        if (storeAuth.GetId() === currentBaId) {
            await fetchUserFilter()
        }
    }

    const refetchAfterFilterChange = async () => {
        await saveGenealogyFilter()
        StoreGenealogyFilter.resetDirty()
        setGenealogyItems([])
        setSelectedPeriod('')
        setSearchQuery('')
        setGenealogyLoading(true)
    }

    const resetGenealogy = async () => {
        setIsRefreshing(true)
        StoreGenealogyFilter.setDirty(false)
        StoreGenealogyFilter.setShouldRefetch(false)
        setGenealogyItems([])
        setSelectedPeriod('')
        setSearchQuery('')
        setGenealogyLoading(true)
    }

    useEffect(() => {
        if (genealogyLoading) {
            if (StoreGenealogyFilter.getShouldRefresh()) {
                getGenealogyItems(true).then(() => setIsRefreshing(false))
            } else {
                fetchData()
            }
        }
    }, [genealogyLoading])

    // Watch for filter changes and refetch
    useEffect(() => {
        if (StoreGenealogyFilter.getShouldRefetch()) {
            StoreGenealogyFilter.setShouldRefetch(false)
            // Only refetch if there are actual changes (isDirty)
            if (StoreGenealogyFilter.getIsDirty()) {
                refetchAfterFilterChange()
            }
        }
    }, [StoreGenealogyFilter.shouldRefetch])

    useEffect(() => {
        if (StoreGenealogyFilter.getShouldRefresh()) {
            setGenealogyLoading(true)
        }
    }, [StoreGenealogyFilter.shouldRefresh])

    useEffect(() => {
        if (genealogyItems.length === 0) {
            setGenealogyLoading(true)
        }
    }, [genealogyItems])

    // Refetch when customerId changes
    useEffect(() => {
        setGenealogyItems([])
        setSelectedPeriod('')
        setSearchQuery('')
    }, [customerId])

    useEffect(() => {
        if (selectedPeriod === '' && genealogyPeriods.length > 0) {
            setSelectedPeriod(genealogyPeriods[0])
        }
    }, [selectedPeriod, genealogyPeriods])

    return (
        <GenealogyCardStyled borderradius={storeElement.IsMobile() ? '0px' : '12px'}>
            <GenealogyFilterModal />
            <GenealogyHistoryModal />
            <CardHeader
                title="Genealogy"
                sx={{
                    backgroundColor: '#EAEAEA',
                    padding: '10px 24px',
                    alignItems: 'center',
                    '& .MuiCardHeader-content': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }
                }}
                titleTypographyProps={{
                    fontFamily: 'Poppins',
                    fontWeight: 700,
                    fontSize: '20px',
                    color: '#003B6F'
                }}
                subheader={
                    <Stack
                        sx={{ cursor: 'pointer' }}
                        onClick={e => {
                            e.stopPropagation()
                            StoreGenealogyFilter.setShowFilterModal(true)
                        }}>
                        <GenealogyFilterIcon />
                    </Stack>
                }
                subheaderTypographyProps={{
                    alignItems: 'center',
                    display: 'flex'
                }}
            />
            <CardContent sx={{ padding: '0px!important' }}>
                <Stack sx={{ overflowX: storeElement.IsMobile() ? 'scroll' : 'hidden', width: storeElement.IsMobile() ? storeElement.dynamicWidth : '100%' }}>
                    {genealogyLoading ? (
                        <GenealogyCardSkeleton rowCount={10} />
                    ) : (
                        <Stack
                            display="flex"
                            flexDirection={storeElement.IsMobile() ? 'row-reverse' : 'row'}
                            id="genealogy-card-content"
                            sx={{ width: storeElement.IsMobile() ? 'max-content' : '100%' }}>
                            <Stack sx={{ minWidth: '400px', flex: 1 }}>
                                <Stack sx={{ borderBottom: '1px solid #D1D9E2', height: '50px' }}>
                                    <TextField
                                        placeholder={translate('type_to_search')}
                                        variant="outlined"
                                        fullWidth
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        inputProps={{ sx: { fontFamily: 'Inter', fontWeight: 400, fontSize: '16px', textAlign: 'center' } }}
                                        sx={{
                                            '& fieldset': {
                                                border: '0px'
                                            }
                                        }}
                                    />
                                </Stack>
                                <Stack
                                    textAlign={'center'}
                                    sx={{ borderBottom: '1px solid #D1D9E2', height: '50px', justifyContent: 'space-between', padding: '15px 12px' }}>
                                    <Typography variant="h4" fontFamily={'Inter'} color="#384250" fontWeight={400} fontSize={'16px'}>
                                        {moment().format('DD MMM YYYY HH:mm A')}
                                    </Typography>
                                </Stack>
                                <Stack>
                                    {filteredGenealogyItems.map((item, index) => {
                                        return (
                                            <GenealogyCardItemName
                                                key={item.customer.id.unicity}
                                                item={item}
                                                index={index}
                                                achievementHistory={achievementHistory}
                                                handleFetchDownline={handleFetchDownline}
                                                isDownline={isDownline}
                                                handleGoBack={handleGoBack}
                                                periodIndex={genealogyPeriods.indexOf(selectedPeriod)}
                                            />
                                        )
                                    })}
                                </Stack>
                            </Stack>
                            <Stack sx={{ flex: 1 }}>
                                <Stack
                                    sx={{
                                        padding: '15px 12px',
                                        borderBottom: '1px solid #D1D9E2',
                                        height: '50px',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderLeft: '1px solid #D1D9E2',
                                        borderRight: storeElement.IsMobile() ? '1px solid #D1D9E2' : '0px',
                                        backgroundColor: '#F5F5F5',
                                        flexDirection: 'row'
                                    }}
                                    id="genealogy-card-period-selector">
                                    <Box
                                        onClick={handleNextPeriod}
                                        sx={{
                                            cursor: genealogyPeriods.indexOf(selectedPeriod) > 0 ? 'pointer' : 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            'svg path': {
                                                stroke: genealogyPeriods.indexOf(selectedPeriod) > 0 ? '#003B6F' : undefined,
                                                strokeOpacity: genealogyPeriods.indexOf(selectedPeriod) > 0 ? 1 : 0.3
                                            }
                                        }}>
                                        <DropdownIcon />
                                    </Box>
                                    <Typography variant="h4" fontFamily={'Inter'} color="#384250" fontWeight={400} fontSize={'18px'} textAlign={'center'}>
                                        {selectedPeriod || ''}
                                    </Typography>
                                    <Box
                                        onClick={handlePreviousPeriod}
                                        sx={{
                                            cursor: genealogyPeriods.indexOf(selectedPeriod) < genealogyPeriods.length - 1 ? 'pointer' : 'default',
                                            display: 'flex',
                                            alignItems: 'center',
                                            svg: { transform: 'rotate(180deg)' },
                                            'svg path': {
                                                stroke: genealogyPeriods.indexOf(selectedPeriod) < genealogyPeriods.length - 1 ? '#003B6F' : undefined,
                                                strokeOpacity: genealogyPeriods.indexOf(selectedPeriod) < genealogyPeriods.length - 1 ? 1 : 0.3
                                            }
                                        }}>
                                        <DropdownIcon />
                                    </Box>
                                </Stack>
                                <Stack flexDirection="row" sx={{ width: '100%' }}>
                                    <GenealogyColumn title="CPV" dataKey="cpv" data={filteredMetricsProfileHistory} withBorder flex={1} />
                                    <GenealogyColumn title="AL" dataKey="active_legs" data={filteredMetricsProfileHistory} flex={1} />
                                    <GenealogyColumn title="PV" dataKey="pv" data={filteredMetricsProfileHistory} flex={1} />
                                    <GenealogyColumn title="TV" dataKey="tv" data={filteredMetricsProfileHistory} flex={1} />
                                    <GenealogyColumn
                                        title="OV"
                                        dataKey="ov"
                                        data={filteredMetricsProfileHistory}
                                        flex={1}
                                        withRightBorder={storeElement.IsMobile() ? true : false}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </GenealogyCardStyled>
    )
})

const GenealogyCardStyled = styled(Card)`
    border-radius: ${({ borderradius }) => borderradius || '12px'};
    background-color: #ffffff;
    position: relative;
`

export default GenealogyCard
